import { AutoCompleteDisplay, Avatar, Date, DateRange } from "@/components";
import * as SC from "@/components/Comments/styled";
import * as GlobalS from "@/components/styled";
import { CommentTarget, FieldType } from "@/enums";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetCommentsQuery, useGetTagsQuery } from "@/services";
import { Badge, Descriptions, Tabs, Tag } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import { any, string } from "prop-types";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import { shapeDynamicDetails } from "./helper";
import * as S from "./styled";

const MAX_FIELDS_PER_COLUMN = 7;

const DynamicDetails = ({ formConfig, data, formType }) => {
  const intl = useIntl();
  const { getPropertyValueDisplay, getMultipleValuesDisplay } =
    useFormPropertyValueLookupProvider();

  const [currentTab, setCurrentTab] = useState();

  const fieldsToFilter = [FieldType.Attachment, FieldType.MultiAttachment];

  const details = shapeDynamicDetails(formConfig, data, fieldsToFilter);

  const { data: tags } = useGetTagsQuery();

  const { data: commentsResponse } = useGetCommentsQuery(
    {
      projectId: data.id,
      target: CommentTarget.IntakeProject,
      targetId: data.id,
    },
    {
      skip: !data.id,
    }
  );

  const getDisplayValue = ({ value, name, url, type }) => {
    switch (type) {
      case FieldType.Text:
      case FieldType.TextArea:
      case FieldType.Number:
        return value;
      case FieldType.Switch:
        return intl.formatMessage({
          id: value === true ? "general.yes" : "general.no",
        });
      case FieldType.DropDown:
        return getPropertyValueDisplay(formType, _.camelCase(name), value);
      case FieldType.MultiSelectDropDown:
        return getMultipleValuesDisplay(formType, _.camelCase(name), value);
      case FieldType.Date:
        return <Date value={value} />;
      case FieldType.DateRange:
        return <DateRange start={value?.[0]} end={value?.[1]} />;
      case FieldType.AutoComplete:
      case FieldType.MultiSelectAutoComplete:
        return <AutoCompleteDisplay url={url} value={value} />;
      default:
        return intl.formatMessage({ id: "general.noData" });
    }
  };

  const calculateColumns = (numFields) =>
    Math.ceil(numFields / MAX_FIELDS_PER_COLUMN);

  const calculateWidth = (numFields) => {
    const colWidth = 250;
    const numColumns = calculateColumns(numFields);

    return colWidth * numColumns;
  };

  const createCommentTags = (comment) => {
    if (comment.tag) {
      const tag = tags?.find((tag) => tag.id === comment.tag);
      if (tag) {
        return (
          <>
            {comment.text}
            <Tag className="ml-2" color={tag?.color}>
              {tag?.label}
            </Tag>
          </>
        );
      }
    }
    return <>{comment.text}</>;
  };

  const dynamicDetails = () => (
    <GlobalS.HorizontalScrollBox>
      {details.map((category) => (
        <Fragment key={category.label.id}>
          {category.fields.length > 0 && (
            <>
              <S.Description
                title={intl.formatMessage({
                  id: category.label.id,
                  defaultMessage: category.label.fallback,
                })}
                layout="vertical"
                colon={false}
                column={calculateColumns(category.fields.length)}
                $width={calculateWidth(category.fields.length)}
              >
                {category.fields.map((field) => (
                  <Descriptions.Item
                    key={field.label.id}
                    label={field.label.fallback}
                  >
                    {getDisplayValue(field)}
                  </Descriptions.Item>
                ))}
              </S.Description>
            </>
          )}
        </Fragment>
      ))}
    </GlobalS.HorizontalScrollBox>
  );

  const comments = () => (
    <GlobalS.HorizontalScrollBox>
      <Fragment
        key={intl.formatMessage({
          id: "general.comments",
        })}
      >
        {commentsResponse?.commentsCount > 0 && (
          <>
            <S.Description
              title={intl.formatMessage({
                id: "general.comments",
              })}
              layout="vertical"
              colon={false}
              column={1}
              $width={"100%"}
            >
              {commentsResponse.comments.map((comment) => (
                <li key={comment.id}>
                  <SC.StyledComment
                    avatar={<Avatar userId={comment.author?.id} />}
                    author={comment.author?.displayName}
                    content={createCommentTags(comment)}
                    datetime={
                      <Date
                        value={dayjs(comment.createdAt).toString()}
                        showTime={true}
                      />
                    }
                  ></SC.StyledComment>
                </li>
              ))}
            </S.Description>
          </>
        )}
      </Fragment>
    </GlobalS.HorizontalScrollBox>
  );

  const tabs = [
    {
      key: "details",
      label: intl.formatMessage({ id: "general.details" }),
      children: dynamicDetails(),
    },
    {
      key: "comments",
      label: (
        <>
          {intl.formatMessage({
            id: "general.comments",
          })}{" "}
          <Badge count={commentsResponse?.commentsCount} showZero />
        </>
      ),
      children: comments(),
    },
  ];

  const handleTabChange = (key) => {
    setCurrentTab(key);
  };

  return (
    <>
      {commentsResponse?.commentsCount > 0 ? (
        <Tabs
          data-test-id="project-intake-editor-tabs"
          size="middle"
          activeKey={currentTab}
          defaultActiveKey={"details"}
          items={tabs}
          onChange={handleTabChange}
        />
      ) : (
        <>{dynamicDetails()}</>
      )}
    </>
  );
};

DynamicDetails.propTypes = {
  /**
   * These currently can't be more specific due to the dynamic nature of formConfig & data
   */
  formConfig: any,
  data: any,
  formType: string,
};

export { DynamicDetails };
